import axios from "axios";
import { BASE_URL } from "../constants";

const REFRESH_URL = `${BASE_URL}api/auth/refresh`;
const LOGOUT_URL = `${BASE_URL}api/auth/logout`;
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let refreshPromise = null;
let configured = false;
const CSRF_STORAGE_KEY = "litss_csrf_token";

// Keep the token isolated to the current browser tab. A hard reload must be
// able to send it to the CSRF-protected refresh endpoint, but closing the tab
// clears it. It is never stored in a cookie or localStorage.
let csrfToken =
  typeof window !== "undefined"
    ? sessionStorage.getItem(CSRF_STORAGE_KEY)
    : null;

export const setCsrfToken = (token) => {
  csrfToken = typeof token === "string" && token ? token : null;
  if (typeof window === "undefined") return;

  if (csrfToken) sessionStorage.setItem(CSRF_STORAGE_KEY, csrfToken);
  else sessionStorage.removeItem(CSRF_STORAGE_KEY);
};

const updateCsrfToken = (data) => {
  setCsrfToken(data?.csrfToken || data?.data?.csrfToken);
};

export const invalidateExpiredSession = () => {
  setCsrfToken(null);
  const sessionType = sessionStorage.getItem("litss_session_type");
  localStorage.clear();
  sessionStorage.clear();
  if (sessionType) {
    window.dispatchEvent(
      new CustomEvent("litss:session-expired", { detail: { sessionType } }),
    );
  }
};

export const getCsrfToken = () => csrfToken;

const isBackendRequest = (url) => {
  try {
    return new URL(url, window.location.origin).href.startsWith(BASE_URL);
  } catch {
    return false;
  }
};

const isAuthEndpoint = (url) => url === REFRESH_URL || url === LOGOUT_URL;

const csrfHeaders = (method, headers = new Headers()) => {
  const result = new Headers(headers);
  const csrfToken = getCsrfToken();

  // The session remains in HttpOnly cookies. This separate, session-scoped
  // token proves that this unsafe request was made by the UI that logged in.
  result.delete("Authorization");
  if (UNSAFE_METHODS.has(method.toUpperCase()) && csrfToken) {
    result.set("X-CSRF-Token", csrfToken);
  }
  return result;
};

const requestRefresh = (nativeFetch = window.fetch.bind(window)) => {
  if (!refreshPromise) {
    const headers = csrfHeaders("POST");
    refreshPromise = nativeFetch(REFRESH_URL, {
      method: "POST",
      credentials: "include",
      headers,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (response.ok) updateCsrfToken(data);
        return { ok: response.ok, data };
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const refreshAccessToken = async (nativeFetch) => (await requestRefresh(nativeFetch)).ok;

// The backend should return a non-sensitive user/org/centre object here, including
// permissions. It is UI state only; authentication remains in HttpOnly cookies.
export const refreshSession = async () => {
  const result = await requestRefresh();
  return result.ok ? result.data : null;
};

export const logout = async () => {
  try {
    await window.fetch(LOGOUT_URL, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders("POST"),
    });
  } catch (error) {
    // A network failure must not leave the local application authenticated.
    console.error("Logout request failed", error);
  } finally {
    setCsrfToken(null);
    // Local storage may contain legacy tokens from earlier releases.
    localStorage.clear();
    sessionStorage.clear();
  }
};

export const configureSecureApi = () => {
  if (configured || typeof window === "undefined") return;
  configured = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = input instanceof Request ? input.url : String(input);
    if (!isBackendRequest(url)) return nativeFetch(input, init);

    const method = (init.method || (input instanceof Request && input.method) || "GET").toUpperCase();
    const request = {
      ...init,
      credentials: "include",
      headers: csrfHeaders(method, init.headers || (input instanceof Request && input.headers)),
    };
    const response = await nativeFetch(input, request);
    const isRetry = init.__secureAuthRetry;

    if (response.status !== 401 || isRetry || isAuthEndpoint(url)) return response;

    const refreshed = await refreshAccessToken(nativeFetch);
    if (!refreshed) {
      invalidateExpiredSession();
      return response;
    }

    return window.fetch(input, { ...init, __secureAuthRetry: true });
  };

  axios.defaults.withCredentials = true;
  axios.interceptors.request.use((config) => {
    const url = config.url || "";
    if (!isBackendRequest(url)) return config;

    const method = (config.method || "get").toUpperCase();
    config.withCredentials = true;
    config.headers = config.headers || {};
    delete config.headers.Authorization;
    const csrfToken = getCsrfToken();
    if (UNSAFE_METHODS.has(method) && csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;
      const url = config?.url || "";
      if (!config || response?.status !== 401 || config.__secureAuthRetry || isAuthEndpoint(url)) {
        return Promise.reject(error);
      }

      const refreshed = await refreshAccessToken(nativeFetch);
      if (!refreshed) {
        invalidateExpiredSession();
        return Promise.reject(error);
      }

      config.__secureAuthRetry = true;
      return axios(config);
    },
  );
};
